import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon, PhotoIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import { useLanguage } from '../contexts/LanguageContext';
import { candidatesAPI, handleApiError } from '../utils/api';

function CandidateModal({ isOpen, onClose, onSuccess, candidate, columnId }) {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    specialty: '',
    score: '',
    title: '',
    career_summary: '',
    strengths: '',
    weaknesses: '',
    column_id: columnId || ''
  });
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const inputClassName = 'w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm text-slate-800 shadow-sm transition focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100';

  useEffect(() => {
    if (candidate) {
      setFormData({
        first_name: candidate.first_name || '',
        last_name: candidate.last_name || '',
        specialty: candidate.specialty || '',
        score: candidate.score || '',
        title: candidate.title || '',
        career_summary: candidate.career_summary || '',
        strengths: candidate.strengths || '',
        weaknesses: candidate.weaknesses || '',
        column_id: candidate.column_id || columnId || ''
      });
      if (candidate.photo_url) {
        setPhotoPreview(candidate.photo_url);
      }
    }
  }, [candidate, columnId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePhotoChange = (e) => {
      const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error(t('candidateModal.errors.photoSize'));
        return;
      }
      setPhoto(file);
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const formDataToSend = new FormData();
      Object.keys(formData).forEach(key => {
        if (formData[key] !== '') {
          formDataToSend.append(key, formData[key]);
        }
      });

      if (photo) {
        formDataToSend.append('photo', photo);
      }

      if (candidate?.id) {
        await candidatesAPI.update(candidate.id, formDataToSend, { suppressErrorToast: true });
      } else {
        await candidatesAPI.createManual(formDataToSend, { suppressErrorToast: true });
      }

      toast.success(candidate ? t('candidateModal.success.updated') : t('candidateModal.success.created'));
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Candidate modal save error:', error);
      toast.error(handleApiError(error, t('candidateModal.errors.saveFailed')));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-slate-950/55 backdrop-blur-sm"
              onClick={onClose}
            />

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="relative max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-2xl border border-slate-200 bg-white shadow-2xl"
            >
              {/* Header */}
              <div className="sticky top-0 z-10 border-b border-slate-200 bg-gradient-to-r from-blue-50 via-indigo-50 to-cyan-50 px-6 py-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900">
                      {candidate ? t('candidateModal.title.edit') : t('candidateModal.title.create')}
                    </h2>
                    <p className="mt-0.5 text-sm text-slate-600">
                      {t('candidateModal.subtitle')}
                    </p>
                  </div>
                  <button
                    onClick={onClose}
                    className="rounded-lg border border-slate-200 bg-white p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-700"
                    aria-label={t('common.close')}
                  >
                    <XMarkIcon className="h-5 w-5 text-gray-500" />
                  </button>
                </div>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-6 p-6">
                {/* Photo */}
                <div className="rounded-xl border border-slate-200 bg-white p-5">
                  <label className="mb-2 block text-sm font-semibold text-slate-700">
                    {t('candidateModal.fields.photo')}
                  </label>
                  <div className="flex items-center space-x-4">
                    <div className="flex-shrink-0">
                      {photoPreview ? (
                        <img
                          src={photoPreview}
                          alt="Preview"
                          className="h-20 w-20 rounded-xl object-cover ring-1 ring-slate-200"
                        />
                      ) : (
                        <div className="flex h-20 w-20 items-center justify-center rounded-xl bg-slate-100 ring-1 ring-slate-200">
                          <PhotoIcon className="h-10 w-10 text-slate-400" />
                        </div>
                      )}
                    </div>
                    <div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handlePhotoChange}
                        className="block w-full text-sm text-gray-500
                          file:mr-4 file:rounded-xl file:border-0
                          file:bg-blue-50 file:px-4 file:py-2
                          file:text-sm file:font-semibold file:text-blue-700
                          hover:file:bg-blue-100"
                      />
                      <p className="mt-1 text-xs text-slate-500">
                        {t('candidateModal.fields.photoHelp')}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  {/* Nom et prénom */}
                  <div className="rounded-xl border border-slate-200 bg-white p-5">
                    <h3 className="mb-3 text-sm font-semibold text-slate-700">
                      {t('candidateModal.sections.identity')}
                    </h3>
                    <div className="grid grid-cols-1 gap-4">
                      <div>
                        <label htmlFor="first_name" className="mb-1 block text-sm font-medium text-slate-700">
                          {t('candidateModal.fields.firstName')} *
                        </label>
                        <input
                          type="text"
                          id="first_name"
                          name="first_name"
                          value={formData.first_name}
                          onChange={handleChange}
                          required
                          className={inputClassName}
                        />
                      </div>
                      <div>
                        <label htmlFor="last_name" className="mb-1 block text-sm font-medium text-slate-700">
                          {t('candidateModal.fields.lastName')} *
                        </label>
                        <input
                          type="text"
                          id="last_name"
                          name="last_name"
                          value={formData.last_name}
                          onChange={handleChange}
                          required
                          className={inputClassName}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Titre et spécialité */}
                  <div className="rounded-xl border border-slate-200 bg-white p-5">
                    <h3 className="mb-3 text-sm font-semibold text-slate-700">
                      {t('candidateModal.sections.positioning')}
                    </h3>
                    <div className="grid grid-cols-1 gap-4">
                      <div>
                        <label htmlFor="title" className="mb-1 block text-sm font-medium text-slate-700">
                          {t('candidateModal.fields.title')}
                        </label>
                        <input
                          type="text"
                          id="title"
                          name="title"
                          value={formData.title}
                          onChange={handleChange}
                          placeholder={t('candidateModal.placeholders.title')}
                          className={inputClassName}
                        />
                      </div>
                      <div>
                        <label htmlFor="specialty" className="mb-1 block text-sm font-medium text-slate-700">
                          {t('candidateModal.fields.specialty')}
                        </label>
                        <input
                          type="text"
                          id="specialty"
                          name="specialty"
                          value={formData.specialty}
                          onChange={handleChange}
                          placeholder={t('candidateModal.placeholders.specialty')}
                          className={inputClassName}
                        />
                      </div>
                      <div>
                        <label htmlFor="score" className="mb-1 block text-sm font-medium text-slate-700">
                          {t('candidateModal.fields.score')}
                        </label>
                        <input
                          type="number"
                          id="score"
                          name="score"
                          value={formData.score}
                          onChange={handleChange}
                          min="0"
                          max="100"
                          className={inputClassName}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Résumé du parcours */}
                <div className="rounded-xl border border-slate-200 bg-white p-5">
                  <label htmlFor="career_summary" className="mb-1 block text-sm font-medium text-slate-700">
                    {t('candidateModal.fields.careerSummary')}
                  </label>
                  <textarea
                    id="career_summary"
                    name="career_summary"
                    value={formData.career_summary}
                    onChange={handleChange}
                    rows={3}
                    placeholder={t('candidateModal.placeholders.careerSummary')}
                    className={inputClassName}
                  />
                </div>

                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  {/* Points forts */}
                  <div className="rounded-xl border border-slate-200 bg-white p-5">
                    <label htmlFor="strengths" className="mb-1 block text-sm font-medium text-slate-700">
                      {t('candidateModal.fields.strengths')}
                    </label>
                    <textarea
                      id="strengths"
                      name="strengths"
                      value={formData.strengths}
                      onChange={handleChange}
                      rows={4}
                      placeholder={t('candidateModal.placeholders.strengths')}
                      className={inputClassName}
                    />
                  </div>

                  {/* Points faibles */}
                  <div className="rounded-xl border border-slate-200 bg-white p-5">
                    <label htmlFor="weaknesses" className="mb-1 block text-sm font-medium text-slate-700">
                      {t('candidateModal.fields.weaknesses')}
                    </label>
                    <textarea
                      id="weaknesses"
                      name="weaknesses"
                      value={formData.weaknesses}
                      onChange={handleChange}
                      rows={4}
                      placeholder={t('candidateModal.placeholders.weaknesses')}
                      className={inputClassName}
                    />
                  </div>
                </div>

                {/* Actions */}
                <div className="flex justify-end space-x-3 border-t border-slate-200 pt-4">
                  <button
                    type="button"
                    onClick={onClose}
                    className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100"
                  >
                      {t('candidateModal.buttons.cancel')}
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {loading ? t('candidateModal.buttons.saving') : (candidate ? t('candidateModal.buttons.update') : t('candidateModal.buttons.create'))}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        </div>
      )}
    </AnimatePresence>
  );
}

export default CandidateModal;
